/**
 * File Upload Hook
 * Handles file uploads to S3 via pre-signed URLs
 */

import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { env } from '@/core/config/env';

type FileUploadOptions = {
  onProgress?: (progress: number) => void;
  maxSizeMB?: number;
};

const API_URL = env.API_BASE_URL;

export function useFileUpload(options: FileUploadOptions = {}) {
  const { getAccessToken } = useAuth();
  const { maxSizeMB = 5 } = options;

  /**
   * Upload file via backend proxy (no CORS issues)
   * Backend handles the S3 upload
   */
  const uploadViaBackend = async (file: File): Promise<string> => {
    const token = await getAccessToken();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);

      // Track upload progress
      if (options.onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            options.onProgress?.(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            // Use publicUrl (full S3 URL) from backend - avoids needing S3 env vars on frontend
            resolve(response.publicUrl || response.fileKey);
          } catch (err) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.message || `Upload failed with status ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('POST', `${API_URL}/files/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  };

  /**
   * Combined upload mutation
   */
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
      }

      // Upload via backend proxy (no CORS issues!)
      return await uploadViaBackend(file);
    },
  });

  /**
   * Delete file mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (fileKey: string) => {
      const token = await getAccessToken();

      const response = await fetch(`${API_URL}/files/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fileKey }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }
    },
  });

  return {
    upload: uploadMutation.mutate,
    uploadAsync: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    uploadError: uploadMutation.error,
    deleteFile: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
}

/**
 * Get public URL for uploaded file.
 * 
 * If fileKey is already a full URL, returns it as-is.
 * Otherwise, constructs the URL from env vars (CDN domain or S3 bucket).
 */
export function getFileUrl(fileKey: string): string {
  // If already a full URL, return as-is
  if (fileKey.startsWith('http://') || fileKey.startsWith('https://')) {
    return fileKey;
  }

  const cdnDomain = import.meta.env.VITE_AWS_CLOUDFRONT_DOMAIN;
  const bucket = import.meta.env.VITE_AWS_S3_BUCKET;
  const region = import.meta.env.VITE_AWS_REGION || 'us-east-1';

  if (cdnDomain) {
    return `https://${cdnDomain}/${fileKey}`;
  }

  if (!bucket) {
    console.warn(
      '[getFileUrl] VITE_AWS_S3_BUCKET env var is not set. Cannot construct S3 URL for file key:',
      fileKey
    );
  }

  return `https://${bucket}.s3.${region}.amazonaws.com/${fileKey}`;
}

